import { useEffect, useRef, useState } from "react";
import { Upload, Images, Star, Trash2, ArrowLeft, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl } from "@/lib/api";
import { Field, Thumb } from "@/components/Bits";

const ACCEPT = "image/jpeg,image/png,image/webp";
const btn = "inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:border-lime";

const srcOf = (item) => (item.file_id ? fileUrl(item.file_id) : item.url);

/**
 * Product imagery: upload from device, pick from the project's files, or keep a
 * pasted/retailer URL. Stores stable file ids so links never expire.
 * `value` is { primary_image, primary_image_file_id, image_file_ids, images }.
 */
export default function ProductImagesField({ projectId, value, onChange, extraUrls = [] }) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [projectImages, setProjectImages] = useState([]);
  const uploadedRef = useRef(new Set());
  const fileRef = useRef();

  useEffect(() => {
    if (!picking || !projectId) return;
    api
      .get("/files", { params: { project_id: projectId } })
      .then((r) => setProjectImages(r.data.filter((f) => (f.content_type || "").startsWith("image/"))))
      .catch(() => setProjectImages([]));
  }, [picking, projectId]);

  // One ordered gallery from both sources; the first entry is the primary image.
  const items = [
    ...(value.image_file_ids || []).map((id) => ({ file_id: id })),
    ...(value.images || []).map((url) => ({ url })),
  ];
  const primarySrc = value.primary_image_file_id ? fileUrl(value.primary_image_file_id) : value.primary_image || "";

  const commit = (next) => {
    const fileIds = next.filter((i) => i.file_id).map((i) => i.file_id);
    const urls = next.filter((i) => i.url).map((i) => i.url);
    const head = next[0];
    onChange({
      image_file_ids: fileIds,
      images: urls,
      primary_image_file_id: head?.file_id || "",
      primary_image: head?.url || "",
    });
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const file of files) {
      if (!ACCEPT.split(",").includes(file.type)) {
        toast.error(`${file.name}: only JPG, PNG or WebP images are supported.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "Renderings");
      if (projectId) fd.append("project_id", projectId);
      try {
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploadedRef.current.add(data.id);
        added.push({ file_id: data.id });
      } catch (err) {
        toast.error(apiError(err));
      }
    }
    if (added.length) {
      commit([...items, ...added]);
      toast.success(added.length > 1 ? `${added.length} images uploaded` : "Image uploaded");
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const choose = (file) => {
    if (items.some((i) => i.file_id === file.id)) return;
    commit([...items, { file_id: file.id }]);
  };

  const remove = async (index) => {
    const item = items[index];
    // Only bin files this form created; files picked from the project stay put.
    if (item.file_id && uploadedRef.current.has(item.file_id)) {
      await api.delete(`/files/${item.file_id}`).catch(() => {});
      uploadedRef.current.delete(item.file_id);
    }
    commit(items.filter((_, i) => i !== index));
  };

  const move = (index, delta) => {
    const next = [...items];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    commit(next);
  };

  const makePrimary = (index) => {
    const next = [...items];
    const [item] = next.splice(index, 1);
    commit([item, ...next]);
  };

  return (
    <div data-testid="product-images-field">
      {primarySrc ? (
        <img
          data-testid="product-primary-preview"
          src={primarySrc}
          alt="Primary product"
          className="aspect-square w-full rounded-sm object-cover"
        />
      ) : (
        <Thumb className="aspect-square w-full" />
      )}

      {!!items.length && (
        <div className="mt-5 grid grid-cols-3 gap-3" data-testid="product-image-thumbs">
          {items.map((item, i) => (
            <div
              key={item.file_id || item.url}
              className={`overflow-hidden rounded-sm border ${i === 0 ? "border-lime" : "border-line"}`}
            >
              <img src={srcOf(item)} alt="" className="aspect-square w-full object-cover bg-line" />
              <div className="flex items-center justify-between gap-1 px-1.5 py-1">
                <button
                  type="button"
                  title={i === 0 ? "Primary image" : "Make primary"}
                  data-testid={`product-image-primary-${i}`}
                  onClick={() => makePrimary(i)}
                  className={`p-1 ${i === 0 ? "text-lime-dark" : "text-stone hover:text-ink"}`}
                >
                  <Star className="h-3.5 w-3.5" strokeWidth={1.5} fill={i === 0 ? "currentColor" : "none"} />
                </button>
                <span className="flex">
                  <button type="button" title="Move left" data-testid={`product-image-left-${i}`} onClick={() => move(i, -1)} className="p-1 text-stone hover:text-ink">
                    <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                  <button type="button" title="Move right" data-testid={`product-image-right-${i}`} onClick={() => move(i, 1)} className="p-1 text-stone hover:text-ink">
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                </span>
                <button type="button" title="Remove" data-testid={`product-image-remove-${i}`} onClick={() => remove(i)} className="p-1 text-stone hover:text-clay">
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <label className={`${btn} cursor-pointer`} data-testid="product-upload-label">
          <Upload className="h-4 w-4" strokeWidth={1.5} /> {busy ? "Uploading…" : "Upload Images"}
          <input
            ref={fileRef}
            data-testid="product-upload-input"
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={upload}
            disabled={busy}
          />
        </label>
        {projectId && (
          <button type="button" data-testid="product-choose-files-btn" onClick={() => setPicking(true)} className={btn}>
            <Images className="h-4 w-4" strokeWidth={1.5} /> Choose from Project Files
          </button>
        )}
      </div>

      {!!extraUrls.length && (
        <div className="mt-5" data-testid="retailer-image-options">
          <span className="overline mb-2 block">From the retailer</span>
          <div className="grid grid-cols-4 gap-2">
            {extraUrls.map((url) => (
              <button
                key={url}
                type="button"
                data-testid="retailer-image-option"
                onClick={() => !items.some((i) => i.url === url) && commit([...items, { url }])}
                className="overflow-hidden rounded-sm border border-line transition-colors hover:border-lime"
              >
                <img src={url} alt="" className="aspect-square w-full object-cover bg-line" />
              </button>
            ))}
          </div>
        </div>
      )}

      <Field label="Or paste an image URL" className="mt-5">
        <input
          data-testid="product-primary-image-input"
          value={value.primary_image || ""}
          onChange={(e) => onChange({ primary_image: e.target.value, primary_image_file_id: "" })}
          className="w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime"
        />
      </Field>

      {picking && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:p-6" data-testid="product-image-picker">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-t-sm bg-white p-6 sm:rounded-sm sm:p-8">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-serif text-xl text-ink">Project images</h3>
              <button
                type="button"
                data-testid="product-image-picker-close"
                onClick={() => setPicking(false)}
                className="rounded-sm border border-line p-1.5 text-stone transition-colors hover:border-fog"
                aria-label="Close"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            {projectImages.length === 0 ? (
              <p className="py-10 text-center text-sm text-fog">No images on this project yet.</p>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {projectImages.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    data-testid={`product-image-pick-${f.id}`}
                    onClick={() => choose(f)}
                    className={`overflow-hidden rounded-sm border text-left transition-colors ${
                      items.some((i) => i.file_id === f.id) ? "border-lime" : "border-line hover:border-fog"
                    }`}
                  >
                    <img src={fileUrl(f.id)} alt={f.original_filename} className="aspect-[4/3] w-full object-cover bg-line" />
                    <span className="block truncate px-3 py-2 text-xs text-stone">{f.original_filename}</span>
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => setPicking(false)}
              className="mt-7 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
